import useMethodStore, { useMethodActions } from "@/store/MethodStore";
import DockLauncher from "./DockLauncher";
import { MdOpenInNew, MdSearch } from "react-icons/md";
import { ApplicationName, AppState } from "@/lib/types";
import useSettingsStore from "@/store/SettingsStore";
import { Separator } from "../ui/separator";
import useDockStore from "@/store/dockStore";
import useApps from "@/hooks/useApps";
import ContextContainer from "../ContextContainer";
import { ContextMenuItem } from "../ui/context-menu";
import { BsPinAngleFill } from "react-icons/bs";
import { RiUnpinFill } from "react-icons/ri";
import useShortcuts from "@/hooks/useShortcuts";

function DockLaunchers() {

  const setActiveTab = useSettingsStore(state => state.setActiveTab)
  const toggleWidgetPanel = useSettingsStore(state => state.toggleWidgetPanel)
  const widgetPanel = useSettingsStore(state => state.widgetPanel)

  const removePinnedApp = useDockStore(state => state.removePinnedApp)
  const addPinnedApp = useDockStore(state => state.addPinnedApp)
  const pinnedApps = useDockStore(state => state.pinnedApps)

  const { setFocusedWindow, setShowBSOD, toggleAskMe, setIsSearchBarOpen, setSettingsState } = useMethodActions()
  const askMeState = useMethodStore(state => state.askMeState)
  const settingsState = useMethodStore(state => state.settingsState)

  const isSmallScreen = window.matchMedia("(max-width: 640px)").matches
  const isMediumScreen = window.matchMedia("(max-width: 768px)").matches

  const handleDockActions = (stateSetter: ((state: AppState) => void), appState: AppState, appName: ApplicationName) => {
    stateSetter(appState === "floating" ? "minimized" : "floating"); setFocusedWindow(appName)
    if (Math.random() < 1 / 15) {
      setShowBSOD(true)
    }
  }

  const toggleAppState = (appName: ApplicationName, stateSetter: (state: AppState) => void) => {
    stateSetter("floating");
    setFocusedWindow(appName);
    if (Math.random() < 1 / 15) {
      setShowBSOD(true)
    }
  };

  const shortcuts = useShortcuts()
  const apps = useApps()

  return (
    <>
      {/* pinned apps */}
      {!isMediumScreen && apps.filter(({ name }) => pinnedApps.includes(name)).map(({ name, state, setState, icon }) => (
        <ContextContainer key={name} trigger={
          <DockLauncher
            alwaysShow
            key={name}
            name={name}
            appState={state}
            onClickHandler={name === "Settings" ? () => {
              if (settingsState === "closed") setActiveTab("general");
              handleDockActions(setSettingsState, settingsState, "Settings");
            } : () => handleDockActions(setState, state, name)}
            icon={icon}
          />
        }>
          <ContextMenuItem onClick={() => toggleAppState(name, setState)}>
            <MdOpenInNew className="text-lg" />
            <span className="ml-3">Open</span>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => removePinnedApp(name)}>
            <RiUnpinFill className="text-lg" />
            <span className="ml-3">Unpin</span>
          </ContextMenuItem>
        </ContextContainer>
      ))}
      {!isMediumScreen && shortcuts.filter(({ name }) => pinnedApps.includes(name)).map(({ name, icon, onClickHandler, contextMenu }) => (
        <ContextContainer key={name} trigger={
          <DockLauncher
            alwaysShow
            key={name}
            name={name}
            onClickHandler={onClickHandler}
            icon={icon}
          />
        }>
          {contextMenu.map(({ icon, name, onClickHandler }) => (
            <ContextMenuItem key={name} onClick={onClickHandler}>
              {icon}
              <span className="ml-3">{name}</span>
            </ContextMenuItem>
          ))}
          <ContextMenuItem onClick={() => removePinnedApp(name)}>
            <RiUnpinFill className="text-lg" />
            <span className="ml-3">Unpin</span>
          </ContextMenuItem>
        </ContextContainer>
      ))}
      {!isMediumScreen && apps.filter(({ name }) => pinnedApps.includes(name)).length > 0 && <Separator orientation="vertical" className="bg-zinc-500/65 h-12 my-2" />}
      {/* not pinned apps */}
      {!isSmallScreen && apps.filter(({ name }) => !pinnedApps.includes(name)).map(({ name, state, setState, icon }) => (
        <ContextContainer
          key={name}
          trigger={
            <DockLauncher
              name={name}
              appState={state}
              onClickHandler={name === "Settings" ? () => {
                if (settingsState === "closed") setActiveTab("general");
                handleDockActions(setSettingsState, settingsState, "Settings");
              } : () => handleDockActions(setState, state, name)}
              icon={icon}
            />
          }>
          <ContextMenuItem onClick={() => toggleAppState(name, setState)}>
            <MdOpenInNew className="text-lg" />
            <span className="ml-3">Open</span>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => addPinnedApp(name)}>
            <BsPinAngleFill className="text-lg" />
            <span className="ml-3">Pin to dock</span>
          </ContextMenuItem>
        </ContextContainer>
      ))}
      {!isMediumScreen && <Separator orientation="vertical" className="bg-zinc-500/65 h-12 my-2" />}
      {/* searchbox */}
      <ContextContainer trigger={
        <DockLauncher
          alwaysShow
          booleanAppState={askMeState}
          onClickHandler={toggleAskMe}
          icon={<img src="./apps/icons8-ai-48.png" className="w-12" />}
          name="Ask Dhvanit (ctrl + c)"
        />
      }>
        <ContextMenuItem onClick={toggleAskMe}>
          <MdOpenInNew className="text-lg" />
          <span className="ml-3">Open</span>
        </ContextMenuItem>
      </ContextContainer>
      <ContextContainer trigger={
        <DockLauncher
          alwaysShow
          booleanAppState={widgetPanel}
          onClickHandler={toggleWidgetPanel}
          icon={<img src="./apps/icons8-widget-48.png" className="w-12" />}
          name="Widgets (ctrl + alt + w)"
        />
      }>
        <ContextMenuItem onClick={toggleWidgetPanel}>
          <MdOpenInNew className="text-lg" />
          <span className="ml-3">Open</span>
        </ContextMenuItem>
      </ContextContainer>
      <ContextContainer trigger={
        <DockLauncher
          alwaysShow
          onClickHandler={() => setIsSearchBarOpen(true)}
          icon={<MdSearch className="text-gray-300" />}
          name="Search (ctrl + k)"
        />
      }>
        <ContextMenuItem onClick={() => setIsSearchBarOpen(true)}>
          <MdSearch className="text-lg" />
          <span className="ml-3">Search</span>
        </ContextMenuItem>
      </ContextContainer>
    </>
  )
}

export default DockLaunchers